import { Alert, Button, EmptyState, formatDate, todayInArgentina } from '@catering/ui';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import type { JSX } from 'react';

import { useCurrentUser } from '../auth/use-current-user';
import { listDeliveryNotes } from '../delivery-notes/api';
import { toPortalStatusLabel } from '../delivery-notes/status-label';
import { CountdownTimer } from './countdown-timer';
import { describeOrderError } from './error-messages';
import { hasOrderPermission } from './order-permissions';
import { useOrderList } from './use-order-list';

function addDays(isoDate: string, days: number): string {
  const date = new Date(`${isoDate}T00:00:00.000Z`);

  date.setUTCDate(date.getUTCDate() + days);

  return date.toISOString().slice(0, 10);
}

function tomorrowIso(): string {
  return addDays(todayInArgentina(), 1);
}

interface QuickActionsProps {
  canCreate: boolean;
  canManageTemplates: boolean;
  /** Next to an existing order the create action says it is for another date, so it does not read as a duplicate. */
  hasOrder?: boolean;
}

/**
 * "Cargar pedido" in the empty state; next to an existing order it becomes
 * "Cargar pedido para otra fecha" so it does not invite a duplicate. It lives
 * here instead of the bottom tab bar (§8.2: max 4 tab-bar items).
 */
function QuickActions({ canCreate, canManageTemplates, hasOrder = false }: QuickActionsProps): JSX.Element | null {
  if (!canCreate && !canManageTemplates) {
    return null;
  }

  return (
    <div className="flex flex-col gap-2 sm:flex-row">
      {canCreate ? (
        <Link to="/orders/new" className="inline-flex">
          {hasOrder ? (
            <Button variant="secondary">Cargar pedido para otra fecha</Button>
          ) : (
            <Button>Cargar pedido</Button>
          )}
        </Link>
      ) : null}
      {canManageTemplates ? (
        <Link to="/orders/templates" className="inline-flex">
          <Button variant="secondary">Plantillas</Button>
        </Link>
      ) : null}
    </div>
  );
}

type NextOrderAction = 'edit' | 'request-change' | 'view';

/**
 * What the customer can still do with an order: edit it while the cutoff is
 * open, ask for a change once operations confirmed it, or only look at it in
 * between (cutoff passed, confirmation pending).
 */
function nextOrderAction(order: { status: string; cutoffAt: string }): NextOrderAction {
  if (order.status === 'CONFIRMED') {
    return 'request-change';
  }

  const cutoffOpen = new Date(order.cutoffAt).getTime() > Date.now();

  return cutoffOpen && (order.status === 'DRAFT' || order.status === 'SUBMITTED') ? 'edit' : 'view';
}

/**
 * `portal-orders/spec.md` → RF-06-01/04/06: the customer's order for the
 * next service day, front and center, with its countdown. `design.md`
 * §6.3: `/orders` is the portal's home, not a listing of everything.
 */
export function HomePage(): JSX.Element {
  const navigate = useNavigate();
  const serviceDate = tomorrowIso();
  const orders = useOrderList({ serviceDate });
  const upcomingOrders = useOrderList({ dateFrom: addDays(serviceDate, 1), pageSize: 3 });
  const deliveryNotes = useQuery({
    queryKey: ['portal-delivery-notes-home'],
    queryFn: () => listDeliveryNotes({ page: 1, pageSize: 3 }),
  });
  const currentUser = useCurrentUser();
  const roleCodes = currentUser.data?.roleCodes ?? [];
  const canCreate = hasOrderPermission(roleCodes, 'order.create');
  const canUpdate = hasOrderPermission(roleCodes, 'order.update');
  const canManageTemplates = hasOrderPermission(roleCodes, 'order.template.manage');

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-4">
        <h1 className="text-lg font-semibold text-slate-900">Tu próximo pedido</h1>

        {orders.isPending ? (
          <p className="text-sm text-slate-500">Cargando…</p>
        ) : orders.isError ? (
          <Alert tone="danger">
            No pudimos cargar tu pedido de mañana.
            <div className="mt-2">
              <Button size="sm" onClick={() => void orders.refetch()}>
                Reintentar
              </Button>
            </div>
            <p className="mt-1 text-xs text-slate-500">{describeOrderError(orders.error)}</p>
          </Alert>
        ) : orders.data.items.length === 0 ? (
          <EmptyState
            headingLevel="h2"
            title="Todavía no cargaste un pedido para mañana"
            description="Cargá uno nuevo; en la pantalla de carga podés repetir el último que hiciste para esa sede."
            action={<QuickActions canCreate={canCreate} canManageTemplates={canManageTemplates} />}
          />
        ) : (
          <>
          {orders.data.items.map((order) => (
          <div key={order.id} className="flex flex-col gap-3 rounded-lg border border-slate-200 bg-white p-4">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-slate-900">
                {order.siteName}
              </span>
              <CountdownTimer cutoffAt={order.cutoffAt} />
            </div>
            <p className="text-sm text-slate-600">
              {formatDate(order.serviceDate)} ·{' '}
              {order.totalQuantity} unidades cargadas
            </p>
            {(() => {
              const action = nextOrderAction(order);
              const openDetail = (): void =>
                void navigate({ to: '/orders/$orderId', params: { orderId: order.id } });

              if (action === 'edit') {
                return canUpdate ? (
                  <Button
                    onClick={() =>
                      void navigate({
                        to: '/orders/$orderId/edit',
                        params: { orderId: order.id },
                      })
                    }
                  >
                    Editar
                  </Button>
                ) : null;
              }

              if (action === 'request-change') {
                return canUpdate ? (
                  <Button onClick={openDetail}>Solicitar cambio</Button>
                ) : (
                  <Button variant="secondary" onClick={openDetail}>
                    Ver pedido
                  </Button>
                );
              }

              return (
                <>
                  <p className="text-sm text-slate-500">
                    Ya pasó la hora de corte: tu pedido se está confirmando. Cuando quede
                    confirmado vas a poder solicitar un cambio.
                  </p>
                  <Button variant="secondary" onClick={openDetail}>
                    Ver pedido
                  </Button>
                </>
              );
            })()}
          </div>
          ))}
          <QuickActions canCreate={canCreate} canManageTemplates={canManageTemplates} hasOrder />
          </>
        )}
      </div>

      <section className="flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <h2 className="text-base font-semibold text-slate-900">Próximos pedidos</h2>
          <Link to="/orders/history" className="text-sm font-medium text-primary-700">
            Ver todos los pedidos
          </Link>
        </div>

        {upcomingOrders.isPending ? (
          <p className="text-sm text-slate-500">Cargando…</p>
        ) : upcomingOrders.isError ? (
          <Alert tone="danger">
            No pudimos cargar tus próximos pedidos.
            <div className="mt-2">
              <Button size="sm" onClick={() => void upcomingOrders.refetch()}>
                Reintentar
              </Button>
            </div>
          </Alert>
        ) : upcomingOrders.data.items.length === 0 ? (
          <p className="text-sm text-slate-500">
            No tenés más pedidos programados por ahora.
          </p>
        ) : (
          <ul className="flex flex-col divide-y divide-slate-200 rounded-lg border border-slate-200 bg-white">
            {upcomingOrders.data.items.map((order) => (
              <li key={order.id} className="flex items-center justify-between px-4 py-3 text-sm">
                <span className="font-medium text-slate-900">{order.siteName}</span>
                <span className="text-slate-600">{formatDate(order.serviceDate)}</span>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <h2 className="text-base font-semibold text-slate-900">Últimas entregas</h2>
          <Link to="/portal/delivery-notes" className="text-sm font-medium text-primary-700">
            Ver todos los remitos
          </Link>
        </div>

        {deliveryNotes.isPending ? (
          <p className="text-sm text-slate-500">Cargando…</p>
        ) : deliveryNotes.isError ? (
          <Alert tone="danger">
            No pudimos cargar tus últimas entregas.
            <div className="mt-2">
              <Button size="sm" onClick={() => void deliveryNotes.refetch()}>
                Reintentar
              </Button>
            </div>
          </Alert>
        ) : deliveryNotes.data.items.length === 0 ? (
          <p className="text-sm text-slate-500">Todavía no tenés remitos registrados.</p>
        ) : (
          <ul className="flex flex-col divide-y divide-slate-200 rounded-lg border border-slate-200 bg-white">
            {deliveryNotes.data.items.map((note) => (
              <li key={note.id} className="flex items-center justify-between px-4 py-3 text-sm">
                <div className="flex flex-col gap-1">
                  <span className="font-medium text-slate-900">{note.noteNumber}</span>
                  <span className="text-slate-600">{formatDate(note.serviceDate)}</span>
                </div>
                <span className="text-right text-slate-900">
                  {toPortalStatusLabel(note.status, {
                    receiverName: note.signature?.receiverName,
                    signedAt: note.signature?.signedAt,
                    voidReason: note.voidReason,
                  })}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
